import React, { useState } from 'react';
import { Plus, Filter, Search, List, Grid, Mail, Archive, Megaphone, Download, Upload, ChevronDown, Calendar, DollarSign, TrendingUp } from 'lucide-react';
import EmptyState from '../components/EmptyState';

const statusOptions = [
  { label: 'All Campaigns', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Draft', value: 'draft' },
  { label: 'Completed', value: 'completed' }
];

export default function Marketing() {
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const campaigns = [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Marketing</h1>
          <p className="mt-1 text-sm text-gray-500">Manage your marketing campaigns and promotions</p>
        </div>
        <button className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Create Campaign
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Active Campaigns</p>
              <p className="mt-1 text-lg font-medium text-gray-900">12</p>
            </div>
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Megaphone className="h-4 w-4 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Reach</p>
              <p className="mt-1 text-lg font-medium text-gray-900">24.5k</p>
            </div>
            <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Conversion Rate</p>
              <p className="mt-1 text-lg font-medium text-gray-900">3.2%</p>
            </div>
            <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Scheduled</p>
              <p className="mt-1 text-lg font-medium text-gray-900">8</p>
            </div>
            <div className="h-8 w-8 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Calendar className="h-4 w-4 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-[#e1e3e5]">
        <div className="px-4 py-3 border-b border-[#e1e3e5] flex justify-between items-center">
          <div className="relative">
            <button
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 focus:outline-none"
            >
              {selectedStatus.label}
              <ChevronDown className={`ml-1.5 h-4 w-4 transition-transform duration-200 ${isStatusOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isStatusOpen && (
              <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedStatus(option);
                        setIsStatusOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        selectedStatus.value === option.value
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      role="menuitem"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-9 pr-4 py-1.5 text-xs bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
            <button className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100">
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Filter
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center h-96">
          <EmptyState
            icon={<Megaphone className="w-8 h-8 text-gray-400" />}
            title="No campaigns yet"
            description="Start creating marketing campaigns to promote your business."
            actionLabel="Create your first campaign"
            onAction={() => {}}
          />
        </div>
      </div>
    </div>
  );
}